import {
  CompaniesInfiniteSelectField,
  FloatingFormItem,
} from '@/components/_common';
import { useNumberingList } from '@/core/domain/invoice/Numberings/Numberings.hook';
import { useInvoiceSettingsByCompany } from '@/core/domain/invoice/settings/Settings.hooks';
import { useInvoiceNumberInutilization } from '@/core/domain/invoiceTransmissionHub/AuxiliaryDocuments/AuxiliaryDocuments.hooks';
import { InutilizationNumberRequestDocumentType } from '@/core/domain/invoiceTransmissionHub/AuxiliaryDocuments/AuxiliaryDocuments.types';
import { useDefaultEmptyFieldError } from '@/hooks';
import { Col, Form, InputNumber, Modal, Row, Select } from 'antd';
import TextArea from 'antd/lib/input/TextArea';

interface Props {
  opened: boolean;
  onClose: VoidFunction;
}

interface FormValues {
  companyId: number;
  documentType: InutilizationNumberRequestDocumentType;
  startNumber: number;
  endNumber: number;
  justification: string;
  series: number;
}

export default function InvoiceInutilizationModal(props: Props) {
  const [form] = Form.useForm<FormValues>();

  const formValues = Form.useWatch([], form);

  const { data: invoiceSettings } = useInvoiceSettingsByCompany(
    formValues?.companyId,
  );

  const isNFCE =
    formValues?.documentType === InutilizationNumberRequestDocumentType.NFCE;

  const numberingList = useNumberingList({
    configNfcId: isNFCE ? invoiceSettings?.configNfc?.id : undefined,
    configNfeId: !isNFCE ? invoiceSettings?.configNfe?.id : undefined,
  });

  const series = numberingList.data;

  const requiredField = useDefaultEmptyFieldError();

  const invoiceInutilization = useInvoiceNumberInutilization();

  const handleClose = () => {
    form.resetFields();
    props.onClose();
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();

      await invoiceInutilization.mutateAsync(values);

      handleClose();
    } catch {}
  };

  return (
    <Modal
      title="Inutilização de notas"
      open={props.opened}
      onCancel={handleClose}
      onOk={handleSubmit}
      okText={invoiceInutilization.isLoading ? 'Executando' : 'Executar'}
      width={500}
      confirmLoading={invoiceInutilization.isLoading}
    >
      <p>Preencha os campos para inutilização das notas</p>

      <Form form={form}>
        <Row gutter={[16, 16]}>
          <Col span={24}>
            <FloatingFormItem
              label="Empresa"
              name="companyId"
              rules={[requiredField]}
            >
              <CompaniesInfiniteSelectField />
            </FloatingFormItem>
          </Col>

          <Col span={24}>
            <FloatingFormItem
              label="Tipo de nota"
              name="documentType"
              rules={[requiredField]}
              initialValue={InutilizationNumberRequestDocumentType.NFE}
            >
              <Select
                options={[
                  {
                    value: InutilizationNumberRequestDocumentType.NFE,
                    label: InutilizationNumberRequestDocumentType.NFE,
                  },
                  {
                    value: InutilizationNumberRequestDocumentType.NFCE,
                    label: InutilizationNumberRequestDocumentType.NFCE,
                  },
                ]}
              />
            </FloatingFormItem>
          </Col>

          <Col span={24}>
            <FloatingFormItem
              name="series"
              label="Série"
              rules={[requiredField]}
              extra={!formValues?.companyId && 'Selecione uma empresa'}
            >
              <Select
                disabled={!formValues?.companyId}
                options={series?.map((serie) => ({
                  value: Number(serie.series),
                  label: serie.series,
                }))}
              />
            </FloatingFormItem>
          </Col>

          <Col span={12}>
            <FloatingFormItem
              name="startNumber"
              label="Numeração inicial"
              rules={[
                requiredField,
                { type: 'number', min: 1, message: 'Valor mínimo: 1' },
              ]}
            >
              <InputNumber style={{ width: '100%' }} min={1} />
            </FloatingFormItem>
          </Col>

          <Col span={12}>
            <FloatingFormItem
              name="endNumber"
              label="Numeração final"
              rules={[
                requiredField,
                { type: 'number', min: 1, message: 'Valor mínimo: 1' },
              ]}
            >
              <InputNumber style={{ width: '100%' }} min={1} />
            </FloatingFormItem>
          </Col>

          <Col span={24}>
            <FloatingFormItem
              name="justification"
              label="Justificativa"
              rules={[
                requiredField,
                {
                  type: 'string',
                  min: 15,
                  message: 'Mínimo de 15 caracteres ',
                },
              ]}
            >
              <TextArea showCount maxLength={255} minLength={15} rows={6} />
            </FloatingFormItem>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
}
